import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useAdminAuth } from './useAdminAuth';
import { useIsMaster } from './useIsMaster';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';

export interface CreditsByType {
  GRATUITO_CADASTRO: number;
  BONUS_INDICADO: number;
  INDICACAO: number;
  PLANO_MENSAL: number;
}

export interface CreditsData {
  creditsUsed: number;
  creditsLimit: number;
  extraCredits: number;
  totalAvailable: number;
  percentageUsed: number;
  canUseCredits: boolean;
  creditsByType: CreditsByType;
}

interface DebitResult {
  success: boolean;
  master?: boolean;
  debited?: number;
  saldo_antes?: number;
  saldo_depois?: number;
  detalhes?: Array<{ tipo: string; debitado: number }>;
  error?: string;
  saldo_total?: number;
  creditos_necessarios?: number;
}

export const useCredits = () => {
  const { user } = useAuth();
  const { isAdmin } = useAdminAuth();
  const { isMaster } = useIsMaster();
  const { toast } = useToast();
  const [credits, setCredits] = useState<CreditsData>({
    creditsUsed: 0,
    creditsLimit: 30,
    extraCredits: 0,
    totalAvailable: 30,
    percentageUsed: 0,
    canUseCredits: true,
    creditsByType: {
      GRATUITO_CADASTRO: 30,
      BONUS_INDICADO: 0,
      INDICACAO: 0,
      PLANO_MENSAL: 0,
    },
  });
  const [loading, setLoading] = useState(true);

  const isUnlimited = isAdmin || isMaster;

  const fetchCredits = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Admins e Masters têm créditos ilimitados
    if (isUnlimited) {
      setCredits({
        creditsUsed: 0,
        creditsLimit: Infinity,
        extraCredits: 0,
        totalAvailable: Infinity,
        percentageUsed: 0,
        canUseCredits: true,
        creditsByType: {
          GRATUITO_CADASTRO: Infinity,
          BONUS_INDICADO: 0,
          INDICACAO: 0,
          PLANO_MENSAL: 0,
        },
      });
      setLoading(false);
      return;
    }

    try {
      console.log('[useCredits] Fetching credits for user:', user.id);
      
      // Buscar créditos por tipo da nova tabela
      const { data: creditsByTypeData, error: creditsError } = await supabase
        .from('user_credits')
        .select('credit_type, balance')
        .eq('user_id', user.id);

      if (creditsError) {
        console.error('[useCredits] Error fetching credits by type:', creditsError);
      }

      // Mapear créditos por tipo
      const creditsByType: CreditsByType = {
        GRATUITO_CADASTRO: 0,
        BONUS_INDICADO: 0,
        INDICACAO: 0,
        PLANO_MENSAL: 0,
      };

      creditsByTypeData?.forEach((item) => {
        const type = item.credit_type as keyof CreditsByType;
        if (type in creditsByType) {
          creditsByType[type] = item.balance || 0;
        }
      });

      // Calcular total disponível (soma de todos os tipos)
      const totalAvailable = Object.values(creditsByType).reduce((sum, val) => sum + val, 0);

      // Buscar uso da tabela legacy para compatibilidade
      const { data: usageData } = await supabase
        .from('usage_tracking')
        .select('credits_used, credits_limit')
        .eq('user_id', user.id)
        .single();

      const creditsUsed = usageData?.credits_used || 0;
      const creditsLimit = usageData?.credits_limit || 30;

      // Créditos extras são os que não expiram (indicação + bônus)
      const extraCredits = creditsByType.INDICACAO + creditsByType.BONUS_INDICADO;

      const percentageUsed = totalAvailable > 0 ? (creditsUsed / (totalAvailable + creditsUsed)) * 100 : 0;
      const canUseCredits = totalAvailable > 0;

      console.log('[useCredits] Credits by type:', creditsByType);
      console.log('[useCredits] Total available:', totalAvailable);

      setCredits({
        creditsUsed,
        creditsLimit,
        extraCredits,
        totalAvailable,
        percentageUsed,
        canUseCredits,
        creditsByType,
      });
    } catch (error) {
      console.error('[useCredits] Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkCanUseCredits = async (creditsRequested: number = 1): Promise<boolean> => {
    if (!user) return false;

    try {
      // Usar a nova função v2
      const { data, error } = await supabase.rpc('can_use_credits_v2', {
        p_user_id: user.id,
        p_credits_requested: creditsRequested
      });

      if (error) throw error;

      if (!data) {
        toast({
          title: "Créditos insuficientes",
          description: `Você não tem créditos suficientes. Restam ${credits.totalAvailable} créditos.`,
          variant: "destructive",
        });
      }

      return data || false;
    } catch (error) {
      console.error('Error checking credits permission:', error);
      return false;
    }
  };

  const debitCredits = async (creditsAmount: number, source: string = 'uso_ia') => {
    if (!user) return { success: false, error: 'Usuário não autenticado' };

    try {
      // Usar a nova função v2 que retorna JSONB
      const { data, error } = await supabase.rpc('debit_credits_v2', {
        p_user_id: user.id,
        p_credits_amount: creditsAmount,
        p_source: source
      });

      if (error) throw error;

      console.log('[useCredits] Debit result:', data);

      // Cast para o tipo correto
      const result = data as unknown as DebitResult;

      if (!result?.success) {
        toast({
          title: "Créditos insuficientes",
          description: result?.error === 'sem_creditos_suficientes' 
            ? `Você precisa de ${creditsAmount} créditos, mas tem apenas ${result?.saldo_total || 0}.`
            : 'Erro ao debitar créditos.',
          variant: "destructive",
        });
        return { success: false, error: result?.error };
      }

      await fetchCredits();
      return { 
        success: true, 
        debited: result.debited,
        saldoAntes: result.saldo_antes,
        saldoDepois: result.saldo_depois,
        detalhes: result.detalhes
      };
    } catch (error) {
      console.error('Error debiting credits:', error);
      return { success: false, error: 'Erro interno' };
    }
  };

  // Converter tokens para créditos (1 crédito = 5.000 tokens)
  const tokensToCredits = (tokens: number): number => {
    return Math.ceil(tokens / 5000);
  };

  // Converter segundos de áudio para créditos (2 créditos por minuto iniciado)
  const audioSecondsToCredits = (seconds: number): number => {
    return Math.ceil(seconds / 60) * 2;
  };

  // Converter minutos de áudio para créditos (2 créditos por minuto iniciado)
  const minutesToCredits = (minutes: number): number => {
    return Math.ceil(minutes) * 2;
  };

  // Calcular custo total de uma operação
  // Regras:
  // - Texto: Math.ceil(tokens / 5000)
  // - Imagem: +2 fixo se houver imagem
  // - Áudio: Math.ceil(segundos / 60) * 2 (2 créditos por minuto iniciado)
  const calculateCreditsCost = (
    tokens: number, 
    audioSeconds: number = 0, 
    hasImage: boolean = false
  ): number => {
    const custoTokens = Math.ceil(tokens / 5000);
    const custoImagem = hasImage ? 2 : 0;
    const custoAudio = audioSeconds > 0 ? Math.ceil(audioSeconds / 60) * 2 : 0;
    const custoTotal = custoTokens + custoImagem + custoAudio;
    
    console.log('[useCredits] Cost calculation:', {
      tokens,
      custoTokens,
      audioSeconds,
      custoAudio,
      hasImage,
      custoImagem,
      custoTotal
    });
    
    return custoTotal;
  };

  useEffect(() => {
    if (!user) return;
    
    fetchCredits();

    // Setup realtime subscription for credits updates
    const channel = supabase
      .channel('credits-changes-v2')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_credits',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[useCredits] Realtime update received:', payload);
          fetchCredits();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'usage_tracking',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[useCredits] Usage tracking update received:', payload);
          fetchCredits();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, isUnlimited]);

  return {
    ...credits,
    loading,
    isUnlimited,
    checkCanUseCredits,
    debitCredits,
    refreshCredits: fetchCredits,
    tokensToCredits,
    minutesToCredits,
    audioSecondsToCredits,
    calculateCreditsCost,
  };
};
